import { useEffect, type MutableRefObject } from 'react';
import type { EditorScreen } from '@/store/editorSlice';
import { reachedQuizProgress90 } from '@/utils/analytics/quizProgress90';
import { fireMetaBrowserEvent } from '@/utils/analytics/playerPixelEvents';
import { usePublicVitalsStep } from '@/public/usePublicVitalsStep';
import { markPaywallStepReached } from './paywallStepProof';

/**
 * События показа экрана квиза: метка шага оплаты в адресе, SCREEN_VIEW с
 * пикселем и замер скорости экрана.
 *
 * WEB-3402: вынесено из QuizScreenWizard.tsx без изменений поведения — те же
 * два эффекта и тот же хук замера в том же порядке, на прежнем месте порядка
 * хуков плеера (выше раннего выхода по проверке способа оплаты).
 *
 * Параметры позиционные, а не объектом: имена ключей объекта минификатор не
 * сжимает, а запас веса первого экрана покупателя — десятки байт.
 *
 * @param stepNumber номер шага пути посетителя, с единицы (useQuizVisitorPath)
 * @param screenProgress «экран N из M» для событий — та же функция, что у
 *   остальных событий плеера
 * @param track единая дверь событий плеера (гасит предпросмотр, ретранслирует
 *   в мост)
 * @param perScreen защёлки повторяемых по экрану событий (`EVENT:screenId`)
 */
export function useQuizScreenEvents(
  currentScreen: EditorScreen | undefined,
  isPreview: boolean,
  isCompleted: boolean,
  funnel: { paywallSlug: string; funnelId: string | null } | null | undefined,
  projectId: string,
  quizSlug: string,
  stepNumber: number,
  screenProgress: () => { screenIndex: number; screenTotal: number },
  track: (event: string, payload?: Record<string, unknown>) => void,
  perScreen: MutableRefObject<Set<string>>,
): void {
  // WEB-744 этап 1.5: живой переход квиз→пейволл помечает адресную строку
  // маркером `?s=paywall` (replaceState, НЕ pushState — traffback-ловушка
  // истории не сдвигается, Back ведёт себя как раньше). Благодаря маркеру
  // возврат со Stripe (success/cancel URL строятся из адресной строки в
  // PublicPaywall) и перезагрузка страницы попадают на пейволл, а не на квиз
  // с начала. history.state сохраняется — там живёт traffback-sentinel.
  //
  // WEB-1740: вместе с меткой оставляем след в хранилище браузера. Метка
  // уезжает вместе с пересланной ссылкой, след — нет, и читающая сторона
  // (PublicQuiz) требует ОБА. Иначе получатель чужой ссылки попадал сразу на
  // оплату: без ответов, без лида, без событий квиза.
  useEffect(() => {
    if (!isCompleted || !funnel?.paywallSlug || isPreview) return;
    // ДО выхода по «метка уже стоит»: человек, пришедший по чужой ссылке и
    // честно прошедший квиз, обязан получить свой след — иначе его
    // собственная перезагрузка выкинет его на первый экран.
    markPaywallStepReached(projectId, quizSlug);
    const url = new URL(window.location.href);
    if (url.searchParams.get('s') === 'paywall') return;
    url.searchParams.set('s', 'paywall');
    window.history.replaceState(window.history.state, '', url);
  }, [isCompleted, funnel, isPreview, projectId, quizSlug]);

  // F1d: screen-view tracking hook — must be called before any early return (Rules of Hooks).
  useEffect(() => {
    if (!currentScreen) return;
    // WEB-2182 п.5: предпросмотр закрыт ЯВНО. Раньше `track` выходил по
    // isPreview внутри себя, а вызов пикселя стоял отдельной строкой после
    // него — и не стрелял только потому, что в предпросмотре не загружается
    // сам пиксель. Защита на побочном обстоятельстве держаться не должна.
    if (isPreview) return;
    // WEB-744 этап 1.5: на шаге пейволла (fast-forward или после завершения)
    // экраны квиза посетителю не видны — SCREEN_VIEW/пиксель не стреляют.
    if (isCompleted) return;
    const key = `SCREEN_VIEW:${currentScreen.id}`;
    if (perScreen.current.has(key)) return;
    perScreen.current.add(key);
    track('SCREEN_VIEW', {
      // WEB-1153: наверх — для колонок аналитики, в metadata — для моста.
      ...screenProgress(),
      screenId: currentScreen.id,
      metadata: screenProgress(),
    });
    // Browser pixel: ViewContent. WEB-2182 (решение владельца 17.09) — ОДИН раз
    // за посещение, а не на каждой смене экрана: защёлка внутри
    // fireMetaBrowserEvent, она же переживает перезагрузку страницы.
    //
    // WEB-2181 Б / ревизия Н3: событие уходит на том же ФАКТЕ, что и серверное
    // (`QUIZ_PROGRESS_90`) — когда посетитель взял порог 90 % своего пути.
    // Раньше браузер слал его на первом же просмотре экрана, а сервер — на
    // пороге: одно имя, разные моменты и разные ключи склейки, значит при
    // включённом флаге серверной отправки сеть посчитала бы конверсию дважды.
    const { screenIndex: oneBasedStep, screenTotal } = screenProgress();
    if (reachedQuizProgress90(oneBasedStep, screenTotal)) {
      void fireMetaBrowserEvent('SCREEN_VIEW');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentScreen, projectId, quizSlug, isPreview]);

  /**
   * WEB-1992 (переоткрыта 16.09) — СБОР ПОКАЗАТЕЛЕЙ СКОРОСТИ ПО КАЖДОМУ ЭКРАНУ
   * КВИЗА, А НЕ ОДИН РАЗ НА ВЕСЬ ПОКАЗ.
   *
   * Причина, по которой карточку вернули 15.09: живой проход квиза в четыре
   * шага не отправил ни одного пакета. `PublicQuiz.tsx` заводил сбор ОДИН раз
   * при первой загрузке тела квиза, с зашитым `stepIndex: 0`, который никогда
   * не менялся — а хук `usePublicVitalsStep` пересоздаёт сбор (и досылает
   * накопленное) ИМЕННО на смене контекста в его зависимостях; при неизменном
   * `stepIndex` он не пересоздавался ни разу за весь проход.
   *
   * ЗДЕСЬ, А НЕ В PublicQuiz.tsx — по той же причине, по которой здесь стоит
   * SCREEN_VIEW строкой выше: `QuizScreenWizard` — ЕДИНСТВЕННЫЙ рендерер
   * экранов квиза (и в бою, и в превью конструктора), и это как раз то место,
   * которое ЗНАЕТ настоящий номер текущего экрана (`stepNumber`, тот же самый,
   * что уже едет в SCREEN_VIEW/аналитику строкой выше — вторая копия счётчика
   * шага здесь не заводится). Новый вид экрана квиза получает сбор по
   * построению — сторож `publicVitalsCoverageGuard.ts` держит это для КАЖДОЙ
   * публичной страницы, обходом каталога, а не списком из двух имён.
   *
   * НУМЕРАЦИЯ ШАГА — С ЕДИНИЦЫ, И ОНА ТЕПЕРЬ ОБЩАЯ (WEB-2190). Здесь она
   * такой была всегда (`stepNumber`), а страница пейволла слала 0/1 — поле в
   * пакете и колонка базы одни, смысл разный. Пейволл выровнен (1 — пейволл
   * своей страницей, 2 — пейволл после квиза), уже накопленные строки не
   * переписываются, их отделяет версия нумерации в пакете
   * (`PUBLIC_VITALS_STEP_NUMBERING`) и в колонке `step_numbering`. Правило
   * держит сторож обходом каталога публичных страниц
   * `build-guards/publicVitalsStepNumberingGuard.ts`.
   *
   * ПОЧЕМУ НЕ НА ШАГЕ ПЕЙВОЛЛА (isCompleted). Экраны квиза посетителю не
   * видны (та же оговорка, что у SCREEN_VIEW строкой выше) — вместо них
   * визард рендерит встроенный `PublicPaywallPage`, у которого СВОЯ проводка
   * сбора (`screenType: 'paywall'`, см. `PublicPaywall.tsx`). Завести сбор
   * от имени квиза и здесь значило бы отправить ДВА пакета на один и тот же
   * показ пейволла — находка ТЗ-остатка QA 15.09, п.3: «проверить обратное —
   * не отправляются ли показатели дважды».
   */
  usePublicVitalsStep(
    isPreview || isCompleted || !currentScreen
      ? null
      : {
          projectId,
          slug: quizSlug,
          screenType: 'quiz',
          stepIndex: stepNumber,
        },
  );
}
